import { TabContext } from '@mui/lab';
import { Box } from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';

import {
  ApartmentCharacteristicsData,
  CompletionData,
  SectionStatus,
} from '../../../api/characteristics/types';
import {
  SectionIndexAutocomplete,
  SectionIndexItem,
  scrollToId,
} from '../components/SectionIndexAutocomplete';
import { StyledTab, StyledTabList, StyledTabPanel } from '../styles';
import { ApartmentBuildingSection } from './building';
import { ApartmentConstructionAreasSection } from './constructionAreas';
import { ApartmentEnterpriseSection } from './enterprise';
import { ApartmentEvaluatingUnitSection } from './evaluatingUnit';
import { ApartmentFramesAndFacadesSection } from './framesAndFacades';
import { ApartmentRegionSection } from './region';
import { apartmentSectionIndex } from './sectionIndex';
import { ApartmentWetAndDryAreasSection } from './wetAndDryAreas';

interface ApartmentCharacteristicsProps {
  characteristicsData: ApartmentCharacteristicsData;
  completionData?: CompletionData;
}

const tabs: { key: keyof ApartmentCharacteristicsData; label: string }[] = [
  { key: 'region', label: 'Características e Região' },
  { key: 'evaluating_unit', label: 'Unidade Avaliando' },
  { key: 'frames_and_facades', label: 'Esquadrias e Fachadas' },
  { key: 'wet_and_dry_areas', label: 'Áreas Molhadas e Secas' },
  { key: 'construction_areas', label: 'Áreas de Construção' },
  { key: 'building', label: 'Prédio' },
  { key: 'enterprise', label: 'Empreendimento' },
];

export function ApartmentCharacteristics({
  characteristicsData,
  completionData,
}: ApartmentCharacteristicsProps): JSX.Element {
  const sectionStatus = (
    key: keyof ApartmentCharacteristicsData
  ): SectionStatus | undefined => completionData?.sections[key]?.status;
  const [activeTab, setActiveTab] =
    useState<keyof ApartmentCharacteristicsData>('region');
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (scrollTarget) {
      timeout = setTimeout(() => {
        scrollToId(scrollTarget);
        setScrollTarget(null);
      }, 150);
    }
    return () => clearTimeout(timeout);
  }, [scrollTarget, activeTab]);

  const handleTabChange = (
    _: SyntheticEvent,
    newTab: keyof ApartmentCharacteristicsData
  ): void => {
    setActiveTab(newTab);
  };

  const handleSearchNavigate = (item: SectionIndexItem): void => {
    if (item.tabKey === activeTab) {
      scrollToId(item.sectionId);
      return;
    }
    setActiveTab(item.tabKey as keyof ApartmentCharacteristicsData);
    setScrollTarget(item.sectionId);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <SectionIndexAutocomplete
        options={apartmentSectionIndex}
        onNavigate={handleSearchNavigate}
      />
      <TabContext value={activeTab}>
        <StyledTabList
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-scrollButtons.Mui-disabled': {
              display: 'none',
            },
          }}
        >
          {tabs.map(({ key, label }) => (
            <StyledTab key={key} value={key} label={label} />
          ))}
        </StyledTabList>
        <StyledTabPanel value={activeTab}>
          {activeTab === 'region' && (
            <ApartmentRegionSection
              formData={characteristicsData.region}
              sectionStatus={sectionStatus('region')}
            />
          )}
          {activeTab === 'evaluating_unit' && (
            <ApartmentEvaluatingUnitSection
              formData={characteristicsData.evaluating_unit}
              sectionStatus={sectionStatus('evaluating_unit')}
            />
          )}
          {activeTab === 'frames_and_facades' && (
            <ApartmentFramesAndFacadesSection
              formData={characteristicsData.frames_and_facades}
              sectionStatus={sectionStatus('frames_and_facades')}
            />
          )}
          {activeTab === 'wet_and_dry_areas' && (
            <ApartmentWetAndDryAreasSection
              formData={characteristicsData.wet_and_dry_areas}
              sectionStatus={sectionStatus('wet_and_dry_areas')}
            />
          )}
          {activeTab === 'construction_areas' && (
            <ApartmentConstructionAreasSection
              formData={characteristicsData.construction_areas}
              sectionStatus={sectionStatus('construction_areas')}
            />
          )}
          {activeTab === 'building' && (
            <ApartmentBuildingSection
              formData={characteristicsData.building}
              sectionStatus={sectionStatus('building')}
            />
          )}
          {activeTab === 'enterprise' && (
            <ApartmentEnterpriseSection
              formData={characteristicsData.enterprise}
              sectionStatus={sectionStatus('enterprise')}
            />
          )}
        </StyledTabPanel>
      </TabContext>
    </Box>
  );
}
